import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '../../lib/api';
import {
  customFields as customFieldsApi,
  listAttendants,
  lists as listsApi,
  pipelinesApi,
  products as productsApi,
  tags as tagsApi,
} from '../../lib/resources';
import {
  CF_ENTITY,
  FIXED_FIELDS,
  OPERATORS_BY_KIND,
  OPERATOR_LABELS,
  SOURCE_LABELS,
  customFieldKind,
  type ListOptionsSource,
  type RuleFieldKind,
} from '../../lib/stage-rule-fields';
import type { CustomField, RuleCondition, RuleSource, Stage, StageRule } from '../../types';
import { Button, ErrorNote, Loading, Toggle, compactInputClass, inputClass } from '../ui';

type Option = { id: string; name: string };

const NEW_RULE = (stageId: string): StageRule => ({
  stageId,
  moment: 'EXIT',
  isActive: true,
  combinator: 'ALL',
  conditions: [],
  restrictTargets: false,
  allowedTargetStageIds: [],
  warningMessage: '',
});

const NEW_CONDITION: RuleCondition = { source: 'business', field: 'total', operator: 'exists' };

/** Dados de apoio: opções das listas e os campos adicionais de cada origem. */
function useRuleLookups() {
  const tags = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list() });
  const lists = useQuery({ queryKey: ['lists'], queryFn: () => listsApi.list() });
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list({ take: 200 }),
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const fields = useQuery({
    queryKey: ['custom-fields', 'rules'],
    queryFn: async () => {
      const [lead, company, business] = await Promise.all([
        customFieldsApi.list({ entity: 'lead' }),
        customFieldsApi.list({ entity: 'company' }),
        customFieldsApi.list({ entity: 'business' }),
      ]);
      return { lead: lead.data, company: company.data, business: business.data };
    },
  });

  const options: Record<ListOptionsSource, Option[]> = {
    tags: tags.data?.data ?? [],
    lists: lists.data?.data ?? [],
    products: products.data?.data ?? [],
    attendants: (attendants.data?.data ?? []).map((item) => ({
      id: item.userId,
      name: item.name,
    })),
    kind: [
      { id: 'PERSON', name: 'Pessoa' },
      { id: 'COMPANY', name: 'Empresa' },
    ],
  };
  return { options, fields: fields.data, isLoading: fields.isLoading };
}

type Lookups = ReturnType<typeof useRuleLookups>;

/** O que o campo da condição é: tipo e de onde vêm as opções. */
function describeField(condition: RuleCondition, lookups: Lookups) {
  if (condition.source === 'lead' || condition.source === 'business') {
    const fixed = FIXED_FIELDS[condition.source].find((item) => item.key === condition.field);
    return {
      kind: (fixed?.kind ?? 'text') as RuleFieldKind,
      options: fixed?.options ? lookups.options[fixed.options] : undefined,
    };
  }
  const entity = CF_ENTITY[condition.source]!;
  const field = (lookups.fields?.[entity] ?? []).find((item) => item.id === condition.field);
  return {
    kind: field ? customFieldKind(field) : ('text' as RuleFieldKind),
    options: field?.options?.map((option) => ({ id: option.id, name: option.label })),
  };
}

function fieldsOf(source: RuleSource, lookups: Lookups): Array<{ key: string; label: string }> {
  if (source === 'lead' || source === 'business') return FIXED_FIELDS[source];
  const entity = CF_ENTITY[source]!;
  return (lookups.fields?.[entity] ?? []).map((field: CustomField) => ({
    key: field.id,
    label: field.name,
  }));
}

/**
 * Aba "Condicionais" do Editar pipeline. Edita um rascunho de todas as regras
 * da pipeline e grava de uma vez ("Salvar alterações"), como no DataCrazy.
 */
export function StageRulesEditor({
  pipelineId,
  stages,
}: {
  pipelineId: string;
  stages: Stage[];
}) {
  const lookups = useRuleLookups();
  const saved = useQuery({
    queryKey: ['pipeline-rules', pipelineId],
    queryFn: () => pipelinesApi.rules(pipelineId),
  });
  const [draft, setDraft] = useState<StageRule[] | null>(null);
  const rules = draft ?? saved.data ?? [];
  const dirty = draft !== null;

  const save = useMutation({
    mutationFn: () =>
      // Só os campos do DTO: a API recusa propriedades a mais (id, datas…).
      pipelinesApi.setRules(
        pipelineId,
        rules.map((rule) => ({
          stageId: rule.stageId,
          moment: rule.moment,
          isActive: rule.isActive,
          combinator: rule.combinator,
          conditions: rule.conditions.map(({ source, field, operator, value }) => ({
            source,
            field,
            operator,
            value: value ?? null,
          })),
          restrictTargets: rule.restrictTargets,
          allowedTargetStageIds: rule.restrictTargets ? rule.allowedTargetStageIds : [],
          warningMessage: rule.warningMessage?.trim() || undefined,
        })) as StageRule[],
      ),
    onSuccess: async () => {
      await saved.refetch();
      setDraft(null);
    },
  });

  const update = (index: number, patch: Partial<StageRule>) =>
    setDraft(
      rules.map((rule, position) => (position === index ? { ...rule, ...patch } : rule)),
    );
  const remove = (index: number) => setDraft(rules.filter((_, position) => position !== index));

  if (saved.isLoading || lookups.isLoading) return <Loading />;

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Uma condicional vale na <strong>entrada</strong> ou na <strong>saída</strong> da etapa.
        Se o negócio não cumprir, o movimento é barrado e a mensagem aparece para quem arrastou.
        Nas ações em lote, os barrados são pulados e listados no fim.
      </p>

      {rules.map((rule, index) => (
        <RuleCard
          key={index}
          rule={rule}
          stages={stages}
          lookups={lookups}
          onChange={(patch) => update(index, patch)}
          onRemove={() => remove(index)}
        />
      ))}
      {rules.length === 0 && (
        <p className="rounded-lg bg-slate-50 py-6 text-center text-xs text-slate-400">
          Nenhuma condicional nesta pipeline.
        </p>
      )}

      <div className="flex items-center justify-between gap-2 border-t border-slate-200 pt-4">
        <Button
          onClick={() => setDraft([...rules, NEW_RULE(stages[0]?.id ?? '')])}
          disabled={stages.length === 0}
        >
          + Adicionar condicional
        </Button>
        <div className="flex items-center gap-3">
          {dirty && (
            <span className="text-xs font-medium text-amber-600">Alterações não salvas</span>
          )}
          {dirty && (
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Descartar
            </Button>
          )}
          <Button
            variant="primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </div>
      </div>
      {save.error && (
        <ErrorNote>{errorMessage(save.error, 'Não foi possível salvar')}</ErrorNote>
      )}
    </div>
  );
}

function RuleCard({
  rule,
  stages,
  lookups,
  onChange,
  onRemove,
}: {
  rule: StageRule;
  stages: Stage[];
  lookups: Lookups;
  onChange: (patch: Partial<StageRule>) => void;
  onRemove: () => void;
}) {
  const setCondition = (index: number, patch: Partial<RuleCondition>) =>
    onChange({
      conditions: rule.conditions.map((condition, position) =>
        position === index ? { ...condition, ...patch } : condition,
      ),
    });

  return (
    <div
      className={`space-y-3 rounded-xl border p-4 ${rule.isActive ? 'border-slate-200' : 'border-dashed border-slate-300 opacity-70'}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">Na</span>
        <select
          className={`${compactInputClass} w-32`}
          value={rule.moment}
          onChange={(event) => onChange({ moment: event.target.value as StageRule['moment'] })}
        >
          <option value="ENTRY">entrada</option>
          <option value="EXIT">saída</option>
        </select>
        <span className="text-slate-500">da etapa</span>
        <select
          className={`${compactInputClass} w-48`}
          value={rule.stageId}
          onChange={(event) => onChange({ stageId: event.target.value })}
        >
          {stages.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {stage.name}
            </option>
          ))}
        </select>
        <span className="text-slate-500">o negócio precisa cumprir</span>
        <select
          className={`${compactInputClass} w-36`}
          value={rule.combinator}
          onChange={(event) =>
            onChange({ combinator: event.target.value as StageRule['combinator'] })
          }
        >
          <option value="ALL">todas</option>
          <option value="ANY">qualquer uma</option>
        </select>
        <div className="ml-auto flex items-center gap-3">
          <Toggle
            checked={rule.isActive}
            onChange={(isActive) => onChange({ isActive })}
            label="Ativa"
          />
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-red-600 hover:underline"
          >
            Remover
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {rule.conditions.map((condition, index) => (
          <ConditionRow
            key={index}
            condition={condition}
            lookups={lookups}
            onChange={(patch) => setCondition(index, patch)}
            onRemove={() =>
              onChange({
                conditions: rule.conditions.filter((_, position) => position !== index),
              })
            }
          />
        ))}
        <button
          type="button"
          onClick={() => onChange({ conditions: [...rule.conditions, { ...NEW_CONDITION }] })}
          className="text-xs font-medium text-slate-600 hover:underline"
        >
          + condição
        </button>
      </div>

      {rule.moment === 'EXIT' && (
        <div className="space-y-2 rounded-lg bg-slate-50 p-3">
          <Toggle
            checked={rule.restrictTargets}
            onChange={(restrictTargets) => onChange({ restrictTargets })}
            label="Restringir para quais etapas o negócio pode ir"
          />
          {rule.restrictTargets && (
            <div className="flex flex-wrap gap-1.5">
              {stages
                .filter((stage) => stage.id !== rule.stageId)
                .map((stage) => {
                  const on = rule.allowedTargetStageIds.includes(stage.id);
                  return (
                    <button
                      key={stage.id}
                      type="button"
                      onClick={() =>
                        onChange({
                          allowedTargetStageIds: on
                            ? rule.allowedTargetStageIds.filter((id) => id !== stage.id)
                            : [...rule.allowedTargetStageIds, stage.id],
                        })
                      }
                      className={`rounded-full border px-2.5 py-0.5 text-xs ${
                        on
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 bg-white text-slate-600'
                      }`}
                    >
                      {stage.name}
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      )}

      <input
        className={`${inputClass} mt-0`}
        placeholder="Mensagem quando barrar (opcional)"
        maxLength={500}
        value={rule.warningMessage ?? ''}
        onChange={(event) => onChange({ warningMessage: event.target.value })}
      />
    </div>
  );
}

function ConditionRow({
  condition,
  lookups,
  onChange,
  onRemove,
}: {
  condition: RuleCondition;
  lookups: Lookups;
  onChange: (patch: Partial<RuleCondition>) => void;
  onRemove: () => void;
}) {
  const fields = fieldsOf(condition.source, lookups);
  const { kind, options } = describeField(condition, lookups);
  const operators = OPERATORS_BY_KIND[kind];
  const needsValue = condition.operator !== 'exists' && condition.operator !== 'not_exists';
  const selected = Array.isArray(condition.value)
    ? condition.value
    : condition.value
      ? [String(condition.value)]
      : [];

  return (
    <div className="flex flex-wrap items-start gap-2">
      <select
        className={`${compactInputClass} w-48`}
        value={condition.source}
        onChange={(event) => {
          const source = event.target.value as RuleSource;
          const first = fieldsOf(source, lookups)[0]?.key ?? '';
          onChange({ source, field: first, operator: 'exists', value: null });
        }}
      >
        {(Object.keys(SOURCE_LABELS) as RuleSource[]).map((source) => (
          <option key={source} value={source}>
            {SOURCE_LABELS[source]}
          </option>
        ))}
      </select>
      <select
        className={`${compactInputClass} w-48`}
        value={condition.field}
        onChange={(event) =>
          onChange({ field: event.target.value, operator: 'exists', value: null })
        }
      >
        {fields.length === 0 && <option value="">Nenhum campo</option>}
        {fields.map((field) => (
          <option key={field.key} value={field.key}>
            {field.label}
          </option>
        ))}
      </select>
      <select
        className={`${compactInputClass} w-44`}
        value={operators.includes(condition.operator) ? condition.operator : 'exists'}
        onChange={(event) =>
          onChange({ operator: event.target.value as RuleCondition['operator'], value: null })
        }
      >
        {operators.map((operator) => (
          <option key={operator} value={operator}>
            {OPERATOR_LABELS[operator]}
          </option>
        ))}
      </select>
      {needsValue &&
        (kind === 'list' && options ? (
          <div className="flex min-w-48 flex-1 flex-wrap gap-1">
            {options.map((option) => {
              const on = selected.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    onChange({
                      value: on
                        ? selected.filter((id) => id !== option.id)
                        : [...selected, option.id],
                    })
                  }
                  className={`rounded-full border px-2 py-0.5 text-xs ${
                    on
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-300 text-slate-600'
                  }`}
                >
                  {option.name}
                </button>
              );
            })}
          </div>
        ) : (
          <input
            className={`${compactInputClass} w-40`}
            inputMode={kind === 'number' ? 'decimal' : undefined}
            value={
              condition.value === null || condition.value === undefined
                ? ''
                : String(condition.value)
            }
            onChange={(event) => onChange({ value: event.target.value })}
          />
        ))}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remover condição"
        className="px-1 py-2 text-slate-400 hover:text-red-600"
      >
        ×
      </button>
    </div>
  );
}
